import { useContext, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { CardPrintingDiplomas } from './ui'
import { ICardPrintingDiplomas, ICardPrintingDiplomasContainer, IFieldsError } from './types'

import { useValue } from '@hooks/useValue'
import { usePrintingsDiplomas } from '@api/printingsDiplomas/state'
import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'


type PrintingDiplomasFormState = {
    printStatusID: number | ''
    diplomRegNumber: string
    diplomNumber: string
    diplomDate: string
}

const createPrintingDiplomasFormSnapshot = (state: PrintingDiplomasFormState) => JSON.stringify(state)

/** Контейнер анкеты печати документа */
export const CardPrintingDiplomasContainer = ({isOpen, cardID, fullNameFrom, dataBase, handlerButtonOk, handlerButtonCancel}: ICardPrintingDiplomasContainer) => {
    const notify = useContext(NotificationContext)

    const {isLoadingCard, getCardData, saveCardData, deleteCard} = usePrintingsDiplomas(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        getCardData: state.actionGetPrintingDiplomasCard,
        saveCardData: state.actionSetPrintingDiplomasCardSave,
        deleteCard: state.actionSetPrintingDiplomasDelete,
    })))

    const modalConfirm = useValue<IModalConfirm>({isOpen: false})

    const loadingTitle = useValue<string>('Загрузка анкеты...')
    const fieldsError = useValue<IFieldsError>({})

    const txtPrintStatusID = useValue<number | ''>('')
    const txtPrintStatus = useValue<string>('')
    const txtDiplomRegNumber = useValue<string>('')
    const txtDiplomNumber = useValue<string>('')
    const txtDiplomDate = useValue<string>('')
    const isDisableBtnSave = useValue<boolean>(true)
    const savedFormSnapshotRef = useRef(createPrintingDiplomasFormSnapshot({printStatusID: '', diplomRegNumber: '', diplomNumber: '', diplomDate: ''}))
    const hasSavedChangesRef = useRef(false)
    const isFormReadyRef = useRef(false)

    const getCurrentFormState = (): PrintingDiplomasFormState => ({
        printStatusID: txtPrintStatusID.value,
        diplomRegNumber: txtDiplomRegNumber.value,
        diplomNumber: txtDiplomNumber.value,
        diplomDate: txtDiplomDate.value,
    })
    const applyCardData = (card: {printStatusID: number; printStatusName: string; diplomRegNumber: string; diplomNumber: string; diplomDateIssue: string}) => {
        const formState: PrintingDiplomasFormState = {
            printStatusID: card.printStatusID || '',
            diplomRegNumber: card.diplomRegNumber || '',
            diplomNumber: card.diplomNumber || '',
            diplomDate: card.diplomDateIssue || '',
        }

        txtPrintStatusID.setValue(formState.printStatusID)
        txtPrintStatus.setValue(card.printStatusName || '')
        txtDiplomRegNumber.setValue(formState.diplomRegNumber)
        txtDiplomNumber.setValue(formState.diplomNumber)
        txtDiplomDate.setValue(formState.diplomDate)
        savedFormSnapshotRef.current = createPrintingDiplomasFormSnapshot(formState)
        isFormReadyRef.current = true
        isDisableBtnSave.setValue(true)
    }

    useEffect(() => {
        let isCurrentRequest = true

        if (isOpen) {
            clearCard()
            getCardData(Number(cardID)).then((res) => {
                if (!isCurrentRequest) return
                if (res) applyCardData(res)
            })
        }

        !isOpen && clearCard() // Обнуление данных по анкете

        return () => {
            isCurrentRequest = false
        }
    }, [isOpen, cardID]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = createPrintingDiplomasFormSnapshot(getCurrentFormState())
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    /** Функция обнуления данных по карточке */
    const clearCard = () => {
        fieldsError.setValue({})
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        savedFormSnapshotRef.current = createPrintingDiplomasFormSnapshot({printStatusID: '', diplomRegNumber: '', diplomNumber: '', diplomDate: ''})

        txtPrintStatusID.setValue('')
        txtPrintStatus.setValue('')
        txtDiplomRegNumber.setValue('')
        txtDiplomNumber.setValue('')
        txtDiplomDate.setValue('')
    }

    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handlerButtonOk && handlerButtonOk()
        } else {
            handlerButtonCancel && handlerButtonCancel()
        }
    }
    const handleBtnClose = () => {
        if (isLoadingCard || modalConfirm.value.isOpen) return

        if (isDisableBtnSave.value) {
            closeCard()
            return
        }

        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Сохранение формы',
            contentText: '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?',
            cancelButtonText: 'Продолжить редактирование',
            okButtonText: 'Закрыть без сохранения',
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            handleButtonOk() {closeCard()},
        })
    }


    /** Открытие/закрытие окна подтверждения */
    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText,
            handleButtonCancel() { modalConfirm.setValue({ isOpen: false }) },
            handleButtonOk() {
                loadingTitle.setValue('Возвращаю в раздел итоговой аттестации...')

                deleteCard(Number(cardID)).then((res) => {
                    if (res === 200) {
                        handlerButtonOk && handlerButtonOk()
                        return notify && notifyOpen('Анкета итоговой работы слушателя, успешно возвращена на обработку.', 'success', 1500, notify)
                    }
                }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })

                modalConfirm.setValue({ isOpen: false })
            }
        })
    }

    
    /** Кнопка сохранить в анкете */
    const handlerBtnSave = (isClosedModal: boolean) => {
        const formState = getCurrentFormState()

        saveCardData(Number(cardID), Number(txtPrintStatusID.value), txtDiplomRegNumber.value.trim(), txtDiplomNumber.value.trim(), txtDiplomDate.value.trim()).then((res) => {
            if (res === 200) {
                notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)
                hasSavedChangesRef.current = true

                if (isClosedModal) {
                    handlerButtonOk && handlerButtonOk()
                    return
                }

                savedFormSnapshotRef.current = createPrintingDiplomasFormSnapshot(formState)
                isDisableBtnSave.setValue(true)
                getCardData(Number(cardID)).then((card) => {
                    if (card) applyCardData(card)
                })
            }
        })
    }
    
    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardPrintingDiplomas = {
        isOpen, isLoadingCard, isDisableBtnSave, fullNameFrom, dataBase, fieldsError, loadingTitle,
        txtPrintStatus, txtDiplomRegNumber, txtDiplomNumber, txtDiplomDate, txtPrintStatusID,

        handlerBtnSave, handlerOpenModalConfirm, handleBtnClose,
    }
    
    return <>
        <CardPrintingDiplomas {...propsToComponent} />
        
        <ModalConfirm {...modalConfirm.value} />
    </>
}